import React, { useEffect, useMemo, useState } from 'react';
import { colors } from '../../styles/tokens';
import { IndicadoresService } from '../../services/indicadores.service';
import type { DashboardFiltros, NegadoDetalleRow } from '../../types/indicadores.types';
import { fmtInt } from '../../utils/format';
import {
  aggregatePorDiaRaw,
  aggregatePorTalla,
  derivarKpis,
  DRILL_VB,
  fmtDia,
  serieContinua,
  share,
  tendenciaPuntos,
} from '../../utils/negadosDrill';
import { buildNegadosDrillSheets, exportXlsx } from '../../utils/exportXlsx';
import ExportButton from '../ExportButton';
import { useEsMovil } from '../../hooks/useEsMovil';

export interface NegadosDrillModalProps {
  /** `productId` de la fila de "Productos más negados" sobre la que se hizo clic (Ajuste B #5). */
  productId: string;
  /**
   * Descripción del producto para el título del modal — `IndicadoresScreen`
   * la resuelve buscando `productId` en las filas de `negadosTienda` ya
   * cargadas (mismo array que pinta `NegadosTiendaTable`), así que este
   * componente no necesita re-consultar el catálogo.
   */
  descripcion: string;
  /**
   * Rango + tienda YA activos en Mi tienda (`miTiendaRange`/`miTiendaStore`
   * de `IndicadoresScreen`) — el drill hereda ESE alcance, igual que el
   * equivalente de la vista Global (`DetalleNegadoModal`), para que el
   * desglose por talla/día cuadre con lo que "Productos más negados" ya
   * está mostrando en esta tienda.
   */
  filtros: DashboardFiltros;
  onClose: () => void;
}

const boxMessageStyle: React.CSSProperties = {
  padding: '20px 16px',
  textAlign: 'center',
  fontWeight: 600,
  fontSize: 13,
};

/**
 * Grid de la franja de KPIs (spec §8): 4 columnas en escritorio, 2 en pantallas
 * angostas. Salto EXPLÍCITO 4→2 (media query) en vez de `auto-fit`, que a anchos
 * intermedios podía caer en 3 columnas. Los estilos inline no soportan media
 * queries, así que se inyecta un `<style>` scopeado a `.drill-kpis-grid`.
 */
const DRILL_KPIS_CSS =
  '.drill-kpis-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}' +
  '@media (max-width:560px){.drill-kpis-grid{grid-template-columns:repeat(2,1fr)}}';

/** Ícono "bombillo" (lectura accionable) — SVG a mano, mismo criterio que los íconos de `BodegaCard`/`GraficoHora` (sin librería ni emoji). */
const BulbIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2.05V17h6v-2.24c0-.86.4-1.55 1-2.05A7 7 0 0 0 12 2z" />
  </svg>
);

/**
 * Fila horizontal de "Negados por talla": `{talla}` a la derecha, un track
 * con la barra proporcional al máximo de la serie y el valor `{negados} ·
 * {share}%`. La talla CRÍTICA (más negada) se resalta en `drill.negadoCritico`
 * + un tag interno "más negada"; el resto en `drill.negadoSuave` (spec §4 —
 * como TODO el dato es "negado", el rojo fuerte se reserva para lo crítico).
 */
const TallaBarRow: React.FC<{
  talla: string;
  negados: number;
  max: number;
  total: number;
  esCritica: boolean;
}> = ({ talla, negados, max, total, esCritica }) => {
  const pct = share(negados, total);
  const anchoBarra = max > 0 ? (negados / max) * 100 : 0;
  const colorBarra = esCritica ? colors.drill.negadoCritico : colors.drill.negadoSuave;

  return (
    <div
      data-testid={`talla-row-${talla}`}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}
    >
      <div
        style={{
          width: 34,
          flex: 'none',
          textAlign: 'right',
          fontSize: 12.5,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: esCritica ? colors.drill.negadoTexto : colors.texto.secondary,
        }}
      >
        {talla}
      </div>
      <div
        style={{
          flex: 1,
          height: 22,
          borderRadius: 8,
          background: colors.ringTrack,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${Math.max(anchoBarra, negados > 0 ? 2 : 0)}%`,
            background: colorBarra,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: esCritica ? 6 : 0,
          }}
        >
          {esCritica && (
            <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' }}>más negada</span>
          )}
        </div>
      </div>
      <div
        style={{
          flex: 'none',
          minWidth: 74,
          textAlign: 'left',
          fontSize: 11.5,
          fontWeight: 700,
          color: esCritica ? colors.drill.negadoTexto : colors.texto.secondary,
        }}
      >
        {fmtInt(negados)} · {pct}%
      </div>
    </div>
  );
};

/** Un tile de la franja de KPIs (`drill-kpis`). */
const KpiTile: React.FC<{ testId: string; label: string; value: React.ReactNode; sub: React.ReactNode }> = ({
  testId,
  label,
  value,
  sub,
}) => (
  <div
    data-testid={testId}
    style={{ background: colors.panelBg, borderRadius: 12, padding: '10px 12px' }}
  >
    <div style={{ fontSize: 11, fontWeight: 700, color: colors.texto.tertiary }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 500, marginTop: 3, color: colors.texto.primary }}>{value}</div>
    <div style={{ fontSize: 11, fontWeight: 600, color: colors.texto.tertiary, marginTop: 1 }}>{sub}</div>
  </div>
);

/**
 * Modal del drill "Negados por talla/día" de Mi tienda — rediseño 2026-07
 * (spec del usuario, feature 6). Se abre al hacer clic en una fila de
 * "Productos más negados" (`NegadosTiendaTable`) y responde, en orden: qué
 * tan grave es (franja de KPIs), dónde está el problema (barras horizontales
 * por talla, crítica resaltada), cuándo pasó (tendencia por día con días
 * faltantes en 0 y el pico marcado), y cierra con una lectura accionable.
 *
 * Espejo de `cdr-sa-app` (Task 2) — mismos testids, mismos colores, misma
 * lógica derivada (`utils/negadosDrill.ts`); solo difiere el wrapper de
 * fetch: acá `IndicadoresService.getNegadosDetalle` + `useEffect` (el app usa
 * RTK Query vía `useGetNegadosDetalleQuery`).
 *
 * Cierra con clic en el backdrop, el botón de cerrar o tecla Esc (listener
 * de `keydown` en `document` — el modal no tiene un contenedor propio con
 * foco).
 */
const NegadosDrillModal: React.FC<NegadosDrillModalProps> = ({
  productId,
  descripcion,
  filtros,
  onClose,
}) => {
  const esMovil = useEsMovil();
  const [filas, setFilas] = useState<NegadoDetalleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    IndicadoresService.getNegadosDetalle(productId, filtros)
      .then((rows) => {
        if (!cancelled) {
          setFilas(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Deps en campos PRIMITIVOS de `filtros` (no el objeto completo): mismo
    // criterio que `DetalleNegadoModal` — `filtros` llega como un literal
    // `{...}` que `IndicadoresScreen` re-crea en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, filtros.desde, filtros.hasta, filtros.chain, filtros.branchOffice]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const porTalla = useMemo(() => aggregatePorTalla(filas), [filas]);
  const serie = useMemo(() => serieContinua(aggregatePorDiaRaw(filas)), [filas]);
  const kpis = useMemo(() => derivarKpis(porTalla, serie), [porTalla, serie]);
  const tendencia = useMemo(() => tendenciaPuntos(serie), [serie]);

  const vacio = !kpis || kpis.total === 0;
  const rango = serie.length > 0 ? `${fmtDia(serie[0].fecha)}–${fmtDia(serie[serie.length - 1].fecha)}` : '';
  const maxTalla = porTalla.length > 0 ? Math.max(...porTalla.map((t) => t.negados)) : 0;

  /** Fase D: export a Excel de "Por talla"/"Por día" (misma agregación ya derivada, sin re-fetch). */
  function handleExport() {
    const filename =
      serie.length > 0
        ? `negados-drill_${serie[0].fecha}_${serie[serie.length - 1].fecha}.xlsx`
        : 'negados-drill.xlsx';
    return exportXlsx(buildNegadosDrillSheets(porTalla, serie), filename);
  }

  return (
    <div
      data-testid="negados-drill-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,23,29,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        // En telefono 20 px por lado se comen 40 de los 390: el modal se
        // pega mas al borde para dejarle el ancho a la tabla de dentro.
        padding: esMovil ? 10 : 20,
      }}
    >
      <div
        data-testid="negados-drill-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: 16,
          border: `1px solid ${colors.border}`,
          boxShadow: '0 20px 48px -14px rgba(20,28,54,.45)',
          width: '100%',
          maxWidth: 640,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <style>{DRILL_KPIS_CSS}</style>
        <div
          style={{
            background: colors.drill.marca,
            color: '#fff',
            padding: esMovil ? '12px 14px' : '14px 20px',
            display: 'flex',
            alignItems: 'center',
            // En telefono el titulo se lleva la primera linea entera y los
            // controles (Excel + x) bajan a la segunda: en una sola fila de
            // 390 px el titulo quedaba en dos o tres caracteres. `wrap` lo
            // consigue sin meter ningun nodo nuevo, asi que en escritorio la
            // barra es exactamente la misma fila de siempre.
            flexWrap: esMovil ? 'wrap' : undefined,
            gap: esMovil ? 8 : 14,
          }}
        >
          <div style={{ flex: esMovil ? '1 1 100%' : 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 16, letterSpacing: '.01em' }}>{descripcion || 'Producto'}</div>
            <div style={{ fontSize: 11.5, opacity: 0.72, fontWeight: 600, marginTop: 2 }}>
              Negados por talla y por día{rango ? ` · ${rango}` : ''}
            </div>
          </div>
          <ExportButton testid="negados-drill" variant="dark" onExport={handleExport} disabled={vacio} />
          <button
            type="button"
            data-testid="negados-drill-cerrar"
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: 'rgba(255,255,255,.14)',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontSize: 18,
              lineHeight: 1,
              cursor: 'pointer',
              padding: esMovil ? '0 12px' : '4px 9px',
              // Regla 4: 44 px de area tocable en telefono.
              ...(esMovil ? { minHeight: 44, minWidth: 44, fontSize: 22, marginLeft: 'auto' } : null),
            }}
          >
            ×
          </button>
        </div>

        {loading && (
          <div data-testid="negados-drill-loading" style={{ ...boxMessageStyle, color: colors.texto.tertiary }}>
            Cargando detalle…
          </div>
        )}

        {!loading && error && (
          <div data-testid="negados-drill-error" style={{ ...boxMessageStyle, color: colors.rojo }}>
            No se pudo cargar el detalle. Reintenta.
          </div>
        )}

        {!loading && !error && vacio && (
          <div data-testid="drill-vacio" style={{ ...boxMessageStyle, color: colors.texto.tertiary }}>
            Este producto no tiene negados en el periodo.
          </div>
        )}

        {!loading && !error && !vacio && kpis && (
          <div style={{ overflow: 'auto', padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Franja de KPIs — responde "¿qué tan grave es?" (spec §1). */}
            <div data-testid="drill-kpis" className="drill-kpis-grid">
              <KpiTile
                testId="kpi-total"
                label="Total negados"
                value={fmtInt(kpis.total)}
                sub={`en ${serie.length} días`}
              />
              <KpiTile
                testId="kpi-talla-critica"
                label="Talla más negada"
                value={<span style={{ color: colors.drill.negadoTexto }}>{kpis.tallaCritica.talla}</span>}
                sub={`${fmtInt(kpis.tallaCritica.negados)} negados · ${share(kpis.tallaCritica.negados, kpis.total)}%`}
              />
              <KpiTile
                testId="kpi-dia-pico"
                label="Día pico"
                value={fmtDia(kpis.diaPico.fecha)}
                sub={`${fmtInt(kpis.diaPico.negados)} negados`}
              />
              <KpiTile
                testId="kpi-tallas-afectadas"
                label="Tallas afectadas"
                value={fmtInt(kpis.tallasAfectadas)}
                sub="de la corrida"
              />
            </div>

            {/* Negados por talla — responde "¿dónde está el problema?" (spec §1, §4). */}
            <div>
              <h4 style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 800, color: colors.texto.strong }}>
                Negados por talla
              </h4>
              <div data-testid="drill-por-talla">
                {porTalla.map((t) => (
                  <TallaBarRow
                    key={t.talla}
                    talla={t.talla}
                    negados={t.negados}
                    max={maxTalla}
                    total={kpis.total}
                    esCritica={t.talla === kpis.tallaCritica.talla}
                  />
                ))}
              </div>
            </div>

            {/* Negados por día — responde "¿cuándo pasó?" (spec §1, §4/§6): tendencia área+línea+puntos con días faltantes rellenados en 0 y el pico marcado. */}
            <div>
              <h4 style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 800, color: colors.texto.strong }}>
                Negados por día
              </h4>
              <svg
                data-testid="drill-por-dia"
                viewBox={`0 0 ${DRILL_VB.w} ${DRILL_VB.h}`}
                style={{ width: '100%', height: 'auto', display: 'block' }}
                fontFamily="Manrope"
              >
                <defs>
                  <linearGradient id="negadosAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.drill.negadoCritico} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={colors.drill.negadoCritico} stopOpacity={0} />
                  </linearGradient>
                </defs>

                <line
                  x1={DRILL_VB.padL}
                  y1={DRILL_VB.baseY}
                  x2={DRILL_VB.w - DRILL_VB.padR}
                  y2={DRILL_VB.baseY}
                  stroke={colors.border}
                  strokeWidth={1}
                />

                {tendencia.pts.length > 1 && (
                  <>
                    <path d={tendencia.area} fill="url(#negadosAreaGradient)" stroke="none" />
                    <polyline
                      points={tendencia.linea}
                      fill="none"
                      stroke={colors.drill.negadoCritico}
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </>
                )}

                {tendencia.pts.map((p) => {
                  const esPico = p.fecha === kpis.diaPico.fecha;
                  return (
                    <g key={p.fecha}>
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r={esPico ? 5 : 3.5}
                        fill={colors.drill.negadoCritico}
                        stroke={esPico ? '#fff' : 'none'}
                        strokeWidth={esPico ? 2 : 0}
                      />
                      {p.negados > 0 && (
                        <text
                          x={p.x}
                          y={p.y - (esPico ? 12 : 9)}
                          textAnchor="middle"
                          fontSize={esPico ? 12 : 11}
                          fontWeight={esPico ? 600 : 500}
                          fill={esPico ? colors.drill.negadoTexto : colors.texto.secondary}
                        >
                          {fmtInt(p.negados)}
                        </text>
                      )}
                      <text
                        x={p.x}
                        y={DRILL_VB.baseY + 16}
                        textAnchor="middle"
                        fontSize={11}
                        fontWeight={600}
                        fill={colors.texto.muted}
                      >
                        {fmtDia(p.fecha)}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>

            {/* Lectura accionable — cierra el drill (spec §1, §4). */}
            <div
              data-testid="drill-lectura"
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                background: colors.estatusBotaderoBg,
                color: colors.estatusBotaderoText,
                borderRadius: 12,
                padding: '12px 14px',
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.45,
              }}
            >
              <span style={{ flex: 'none', marginTop: 1 }}>
                <BulbIcon color={colors.estatusBotaderoText} />
              </span>
              <span>
                La talla <strong>{kpis.tallaCritica.talla}</strong> concentra {fmtInt(kpis.tallaCritica.negados)} de{' '}
                {fmtInt(kpis.total)} negados ({share(kpis.tallaCritica.negados, kpis.total)}%) y el{' '}
                <strong>{fmtDia(kpis.diaPico.fecha)}</strong> fue el día con más rechazos ({fmtInt(kpis.diaPico.negados)}).
                Conviene revisar la disponibilidad de la talla {kpis.tallaCritica.talla} en bodega.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NegadosDrillModal;
